import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import {
  PaymentMethodDetailFields,
  PaymentMethodSelect,
  applyPaymentMethodChange,
  emptyPaymentMethodDraft,
  type PaymentMethodDraft,
} from "@/components/sales/PaymentMethodFields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { format } from "date-fns";
import { Loader2, HandCoins, Calculator, Info, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  outstanding: number;
  action: "pay" | "receive";
  partyName: string;
  partyId: number;
  transactionType: string;
  scheduledPaymentId: number;
  onSubmit: (payload: {
    amount: number;
    payment_date: string;
    payment_method: string | null;
    bank_name: string | null;
    transaction_reference_no: string | null;
    pay_order_no: string | null;
    notes: string | null;
    action: "pay" | "receive";
    party_id: number;
    transaction_type: string;
    scheduled_payment_id: number;
  }) => Promise<void>;
};

function todayIso(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function parseAmount(raw: string): number | null {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function ProductPaymentDialog({
  open,
  onOpenChange,
  outstanding,
  action,
  partyName,
  partyId,
  transactionType,
  scheduledPaymentId,
  onSubmit,
}: Props) {
  const [amountInput, setAmountInput] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [methodDraft, setMethodDraft] = useState<PaymentMethodDraft>(emptyPaymentMethodDraft());
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAmountInput(outstanding > 0 ? String(outstanding) : "");
    setPaymentDate(todayIso());
    setMethodDraft(emptyPaymentMethodDraft());
    setNotes("");
    setSaving(false);
    setError(null);
  }, [open, outstanding]);

  const enteredAmount = parseAmount(amountInput) ?? 0;
  const remaining = Math.max(0, outstanding - enteredAmount);
  const paidPercent = outstanding > 0 ? Math.min(100, Math.round((enteredAmount / outstanding) * 100)) : 0;
  const enteredAmountLabel = pkrAmountShortLabel(enteredAmount);

  const applyPreset = (percent: number) => {
    const value = Math.round((outstanding * (percent / 100)) * 100) / 100;
    setAmountInput(String(value));
    setError(null);
  };

  const save = async () => {
    if (saving) return;
    const amount = parseAmount(amountInput);
    if (amount == null) {
      setError(`Enter a valid amount greater than zero.`);
      return;
    }
    if (amount > outstanding + 0.009) {
      setError(`Amount cannot exceed the outstanding balance of ${formatCurrency(outstanding)}.`);
      return;
    }
    if (!paymentDate.trim()) {
      setError("Date is required.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        amount,
        payment_date: paymentDate,
        payment_method: methodDraft.paymentMethod || null,
        bank_name: methodDraft.bankName.trim() || null,
        transaction_reference_no: methodDraft.transactionReferenceNo.trim() || null,
        pay_order_no: methodDraft.payOrderNo.trim() || null,
        notes: notes.trim() || null,
        action,
        party_id: partyId,
        transaction_type: transactionType,
        scheduled_payment_id: scheduledPaymentId,
      });
      onOpenChange(false);
    } catch (err: any) {
      setError(err?.message || "An error occurred while saving the payment.");
    } finally {
      setSaving(false);
    }
  };

  const isPay = action === "pay";
  const title = isPay ? "Record Purchase Payment" : "Record Partner Investment Receipt";
  const subtitle = isPay
    ? "Record a cash/bank payment paid to the supplier for the vehicle base cost or additional costs."
    : "Record a partner cash/bank investment share received by the company.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto p-6">
        {/* Header */}
        <DialogHeader className="pb-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <div className={cn("h-9 w-9 rounded-xl border flex items-center justify-center", 
              isPay ? "bg-amber-500/10 border-amber-500/20 text-amber-600" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
            )}>
              <HandCoins className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-foreground">{title}</DialogTitle>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Profile / Context Card */}
        <div className="flex items-center justify-between bg-muted/30 border border-border/50 rounded-xl p-3 text-xs">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={cn("h-8 w-8 rounded-full border flex items-center justify-center font-bold text-xs shrink-0",
              isPay ? "bg-amber-500/10 border-amber-500/20 text-amber-600" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-600"
            )}>
              <Wallet className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-foreground truncate">{partyName}</p>
              <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">
                {isPay ? "Supplier" : "Deal Partner"}
              </p>
            </div>
          </div>
          <div className="text-right shrink-0 pl-3">
            <p className="text-[10px] uppercase font-semibold text-muted-foreground">Outstanding Balance</p>
            <p className="font-bold text-primary text-sm">{formatCurrency(outstanding)}</p>
          </div>
        </div>

        {/* Split Columns body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
          {/* Left Column: Form Fields */}
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex min-h-5 items-center justify-between gap-2">
                <Label htmlFor="prod-payment-amount" className="text-xs font-semibold text-foreground">
                  {isPay ? "Payment Amount" : "Receipt Amount"} <span className="text-destructive">*</span>
                </Label>
                {enteredAmountLabel ? (
                  <span className="shrink-0 text-xs font-bold tabular-nums text-primary">
                    {enteredAmountLabel}
                  </span>
                ) : null}
              </div>
              <div className="relative">
                <Input
                  id="prod-payment-amount"
                  className="glass-input h-10 pr-12 font-semibold"
                  value={amountInput}
                  disabled={saving}
                  onChange={(e) => {
                    const nextInput = e.target.value;
                    const nextAmount = parseAmount(nextInput);

                    if (nextAmount !== null && nextAmount > outstanding + 0.009) {
                      setError(`Maximum is ${formatCurrency(outstanding)}.`);
                      return;
                    }

                    setAmountInput(nextInput);
                    setError(null);
                  }}
                  placeholder="0.00"
                  inputMode="decimal"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
                  {getActiveCompanyCurrency().code}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <span className="text-[10px] text-muted-foreground self-center mr-1 font-medium">Quick:</span>
                {[25, 50, 100].map((percent) => (
                  <button
                    key={percent}
                    type="button"
                    onClick={() => applyPreset(percent)}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-md border border-border/60 bg-background hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-colors"
                  >
                    {percent === 100 ? "Full" : `${percent}%`}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold text-foreground">Date</Label>
              <DatePartsPicker value={paymentDate} onChange={setPaymentDate} disabled={saving} />
            </div>

            <div className="space-y-2">
              <PaymentMethodSelect
                id="prod-payment-method"
                value={methodDraft.paymentMethod}
                onChange={(method) => setMethodDraft((cur) => applyPaymentMethodChange(cur, method))}
                disabled={saving}
              />
            </div>
            <PaymentMethodDetailFields
              method={methodDraft.paymentMethod}
              bankName={methodDraft.bankName}
              transactionReferenceNo={methodDraft.transactionReferenceNo}
              payOrderNo={methodDraft.payOrderNo}
              onBankNameChange={(v) => setMethodDraft((cur) => ({ ...cur, bankName: v }))}
              onTransactionReferenceChange={(v) => setMethodDraft((cur) => ({ ...cur, transactionReferenceNo: v }))}
              onPayOrderNoChange={(v) => setMethodDraft((cur) => ({ ...cur, payOrderNo: v }))}
              disabled={saving}
            />

            <div className="space-y-2">
              <Label htmlFor="prod-payment-notes" className="text-xs font-semibold text-foreground">Notes</Label>
              <Textarea
                id="prod-payment-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                rows={2}
                disabled={saving}
              />
            </div>

            {error ? <p className="text-sm text-destructive">{error}</p> : null}
          </div>

          {/* Right Column: Live Schedule Preview */}
          <div className="flex flex-col justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-4">
            <div>
              <div className="flex items-center gap-2 pb-3 border-b border-primary/10">
                <Calculator className="w-4 h-4 text-primary" />
                <h4 className="font-bold text-xs uppercase tracking-wider text-foreground">Live Balance Preview</h4>
              </div>

              <div className="space-y-3 pt-3 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Expected Amount</span>
                  <span className="font-bold text-foreground text-sm">{formatCurrency(outstanding)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">This Transaction</span>
                  <span className={cn("font-bold text-sm", isPay ? "text-rose-600" : "text-emerald-600")}>
                    {isPay ? "-" : "+"} {enteredAmount ? formatCurrency(enteredAmount) : "—"}
                  </span>
                </div>
                <div className="flex justify-between items-center border-t border-primary/10 pt-2">
                  <span className="text-muted-foreground">Remaining Balance</span>
                  <span className="font-extrabold text-primary text-sm">
                    {formatCurrency(remaining)}
                  </span>
                </div>

                {enteredAmount > 0 ? (
                  <div className="bg-background/80 rounded-lg border border-primary/20 p-3 space-y-2 mt-2">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-muted-foreground">Transaction Progress</span>
                      <span className="text-primary">{paidPercent}%</span>
                    </div>
                    <div className="h-2 w-full bg-primary/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${paidPercent}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground italic text-center pt-0.5">
                      This will settle {paidPercent}% of the outstanding balance.
                    </p>
                  </div>
                ) : (
                  <div className="bg-background/50 rounded-lg border border-border/50 p-3 text-center text-xs text-muted-foreground italic">
                    Enter transaction amount to view live balance impact.
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-2 border-t border-primary/10">
              <Info className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>General ledger journals and cash/bank accounts are adjusted automatically upon submission.</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="mt-4 border-t border-border/50 pt-3">
          <Button type="button" variant="glass" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="premium" onClick={() => void save()} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              isPay ? "Record Payment" : "Record Receipt"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
